import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import HelpTooltip from '@/components/help/HelpTooltip';
import SectionHelp from '@/components/help/SectionHelp';
import VerticalExampleBox from '@/components/help/VerticalExampleBox';
import helpContent from '@/help/helpContent';
import { friendlyChannelName, friendlyToneName } from '@/lib/channelLabels';
import { friendlyAiProviderName } from '@/lib/providerLabels';
import {
  Brain, Save, Play, Eye, Upload, RotateCcw, Settings2, MessageCircle,
  Shield, Target, Sparkles, AlertTriangle, CheckCircle, Clock, ChevronRight, X, BookOpen
} from 'lucide-react';

const TONES = ['professional', 'friendly', 'formal', 'casual', 'empathetic'];
// 2026-07-06 — 'instagram'/'facebook' removed: there's no actual messaging
// adapter for either (no tenant has ever had a row for them either), so they
// were phantom options that implied a native Meta DM/Messenger integration
// this app doesn't have. Facebook Ads vs Instagram Ads leads are already
// auto-tagged by source at capture time (lead_capture.py) and reply through
// whichever real channel (WhatsApp, etc.) is actually connected — configuring
// "how the AI responds on Facebook" separately never corresponded to anything.
const CHANNELS = ['whatsapp', 'web_chat', 'email', 'voice', 'sms', 'telegram'];
const VAR_HINTS = ['current_date', 'current_year', 'tenant_name', 'vertical', 'assistant_name'];

const LEVEL_FIELDS = [
  {
    key: 'formality_level',
    label: 'Formalidad',
    tooltip: 'Qué tan formal suena tu asistente: del 1 (muy relajado, tutea) al 5 (muy corporativo, usa usted). No cambia qué sabe ni qué puede hacer — solo cómo habla.',
  },
  {
    key: 'persuasion_level',
    label: 'Persuasión',
    tooltip: 'Qué tan insistente es al recomendar o cerrar una venta: del 1 (solo informa) al 5 (muy orientado a conversión). No activa descuentos ni promociones por sí solo.',
  },
  {
    key: 'autonomy_level',
    label: 'Autonomía',
    tooltip: 'Qué tan proactivo es: del 1 (solo responde lo que le preguntan) al 5 (guía la conversación y propone siguientes pasos). No ejecuta acciones reales sin las herramientas activadas.',
  },
];

const DEFAULT_LEVEL_LABELS = {
  formality: {
    1: 'muy informal', 2: 'informal', 3: 'neutro', 4: 'formal', 5: 'muy formal',
  },
  persuasion: {
    1: 'solo informa', 2: 'sugiere', 3: 'equilibrado', 4: 'persuasivo', 5: 'muy persuasivo',
  },
  autonomy: {
    1: 'reactivo', 2: 'poco proactivo', 3: 'equilibrado', 4: 'proactivo', 5: 'muy proactivo',
  },
};

function canPublishProfile(user) {
  return ['tenant_owner', 'tenant_admin'].includes(user?.tenant_role);
}

// Sprint 37 — cada canal necesita un techo distinto (una llamada de voz no
// "piensa" en el mismo largo que un WhatsApp), así que ya no default a 300
// para todos. Antes esto tampoco se aplicaba de verdad en producción — ahora
// sí se respeta al responder, por eso importa que el default no sea el mismo
// número genérico para todos los canales.
const CHANNEL_DEFAULT_MAX_TOKENS = { whatsapp: 500, voice: 1200, email: 800, web_chat: 500, sms: 300, telegram: 500 };
const CHANNEL_DEFAULT_TEMPERATURE = { whatsapp: 0.7, voice: 0.7, email: 0.6, web_chat: 0.7, sms: 0.7, telegram: 0.7 };

function KnowledgeCTA() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  useEffect(() => {
    api.get('/tenant/knowledge/assets?page_size=1').then(r => {
      if ((r.data?.total || 0) === 0) setShow(true);
    }).catch(() => {});
  }, []);
  if (!show) return null;
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2" data-testid="knowledge-cta-banner">
      <BookOpen className="w-4 h-4 text-blue-500 mt-0.5" />
      <div className="text-xs text-blue-700">
        <p>Tu IA todavía no tiene biblioteca de conocimiento. Sube documentos en Biblioteca.</p>
        <button onClick={() => navigate('/tenant/ai/knowledge')} className="underline mt-1 font-medium" data-testid="knowledge-cta-link">Ir a Biblioteca</button>
      </div>
    </div>
  );
}

function SkillsCTA() {
  const navigate = useNavigate();
  return (
    <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 flex items-start gap-2" data-testid="skills-cta-banner">
      <BookOpen className="w-4 h-4 text-violet-500 mt-0.5" />
      <div className="text-xs text-violet-700">
        <p>Convierte este perfil en habilidades reutilizables para tu IA.</p>
        <button onClick={() => navigate('/tenant/ai/skills')} className="underline mt-1 font-medium" data-testid="skills-cta-link">Ir a Habilidades</button>
      </div>
    </div>
  );
}

export default function AITrainingStudio() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('identity');

  useEffect(() => {
    api.get('/tenant/ai-training/profile').then(r => setProfile(r.data)).catch(e => {
      if (e.response?.status === 403) toast.error(formatApiError(e.response?.data?.detail));
      else toast.error('Error cargando perfil');
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <PantallaCargando />;

  const tabs = [
    { k: 'identity', l: 'Identidad', icon: Sparkles },
    { k: 'objectives', l: 'Objetivos', icon: Target },
    { k: 'guardrails', l: 'Límites', icon: Shield },
    { k: 'channels', l: 'Canales', icon: MessageCircle },
    { k: 'simulator', l: 'Simulador', icon: Play },
    { k: 'versions', l: 'Versiones', icon: Clock },
  ];

  return (
    <div className="space-y-5" data-testid="ai-training-studio">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight flex items-center gap-2"><Brain className="w-6 h-6" /> AI Training Studio</h1>
          <p className="text-sm text-[#64748B] mt-1">Configura la identidad, objetivos y reglas de tu asistente IA</p>
        </div>
        <div className="flex gap-2">
          {!profile?.is_persisted && <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">Borrador no guardado</span>}
          {profile?.status === 'draft' && profile?.is_persisted && <span className="text-xs text-slate-600 bg-slate-50 px-2 py-1 rounded">Borrador</span>}
          {profile?.status === 'pending_review' && <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">En revisión</span>}
          {profile?.status === 'published' && <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded">v{profile.current_version} publicada</span>}
        </div>
      </div>

      {/* AI Provider banner */}
      {profile?.ai_providers_status && !profile.ai_providers_status.some(p => p.connected) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2" data-testid="ai-training-no-provider-banner">
          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
          <div className="text-xs text-amber-700">
            <p className="font-medium">No hay provider de IA conectado.</p>
            <button onClick={() => navigate('/tenant/integrations?category=ai')} className="underline mt-1">Conectar en Integraciones</button>
          </div>
        </div>
      )}

      {/* Knowledge Library CTA */}
      <KnowledgeCTA />
      <SkillsCTA />
      <SectionHelp module="training" title={helpContent.training.title} description={helpContent.training.description} whatItDoesNot={helpContent.training.whatItDoesNot} />
      <VerticalExampleBox module="training" />

      <div className="flex gap-1 border-b border-[#E2E8F0] overflow-x-auto">
        {tabs.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${tab === t.k ? 'border-[#F72585] text-[#0F172A]' : 'border-transparent text-[#64748B]'}`}
            data-testid={`tab-${t.k}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.l}
          </button>
        ))}
      </div>

      {tab === 'identity' && <IdentityTab profile={profile} setProfile={setProfile} />}
      {tab === 'objectives' && <ObjectivesTab profile={profile} setProfile={setProfile} />}
      {tab === 'guardrails' && <GuardrailsTab profile={profile} setProfile={setProfile} />}
      {tab === 'channels' && <ChannelsTab profile={profile} />}
      {tab === 'simulator' && <SimulatorTab profile={profile} setProfile={setProfile} />}
      {tab === 'versions' && <VersionsTab />}
    </div>
  );
}

function LevelSelector({ field, value, onChange, labels }) {
  const shortLabels = labels || DEFAULT_LEVEL_LABELS[field.key.replace('_level', '')] || {};
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <label className="text-xs font-medium text-[#64748B]">{field.label}</label>
        <HelpTooltip text={field.tooltip} />
      </div>
      <div className="flex gap-1 flex-wrap">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`text-[10px] px-2 py-1 rounded-lg border transition-colors ${
              value === n
                ? 'bg-[#0F172A] text-white border-[#0F172A]'
                : 'bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#CBD5E1]'
            }`}
            data-testid={`${field.key}-${n}`}
            title={shortLabels[n] || `${n}/5`}
          >
            {n} · {shortLabels[n] || n}
          </button>
        ))}
      </div>
    </div>
  );
}

function IdentityTab({ profile, setProfile }) {
  const [form, setForm] = useState({
    assistant_name: profile?.assistant_name || 'Asistente',
    tone: profile?.tone || 'friendly',
    formality_level: profile?.formality_level || 3,
    persuasion_level: profile?.persuasion_level || 3,
    autonomy_level: profile?.autonomy_level || 3,
    personality_traits: profile?.personality_traits || [],
    system_prompt_template: profile?.system_prompt_template || '',
  });
  const [saving, setSaving] = useState(false);
  const [newTrait, setNewTrait] = useState('');

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/tenant/ai-training/profile', form);
      setProfile(prev => ({ ...prev, ...data }));
      toast.success('Identidad guardada');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addTrait = () => {
    if (newTrait.trim() && form.personality_traits.length < 20) {
      setForm(f => ({ ...f, personality_traits: [...f.personality_traits, newTrait.trim()] }));
      setNewTrait('');
    }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="identity-tab">
      <SectionHelp module="identity" title="la identidad del asistente"
        description="Define quién es tu IA de cara al cliente: su nombre, qué tan formal o relajado suena (Tono), y rasgos concretos que se noten en su forma de hablar (ej. 'entusiasta', 'directo, sin rodeos'). El 'System prompt personalizado' es para instrucciones técnicas de estilo que quieras agregar tú mismo — la mayoría de negocios no lo necesita, el Tono y los rasgos ya cubren lo esencial."
        whatItDoesNot="No decide qué sabe la IA de tu negocio (eso es Biblioteca) ni qué debe evitar hacer (eso es Reglas y límites)." />
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4">
        <div><label className="text-xs font-medium text-[#64748B] mb-1 block">Nombre del asistente</label>
          <Input value={form.assistant_name} onChange={e => setForm(f => ({ ...f, assistant_name: e.target.value }))} maxLength={100} className="text-xs" data-testid="assistant-name-input" /></div>
        <div><label className="text-xs font-medium text-[#64748B] mb-1 block">Tono</label>
          <select value={form.tone} onChange={e => setForm(f => ({ ...f, tone: e.target.value }))} className="text-xs border border-[#E2E8F0] rounded-lg px-3 py-2 w-full" data-testid="tone-select">
            {TONES.map(t => <option key={t} value={t}>{friendlyToneName(t)}</option>)}
          </select></div>
        <div className="space-y-3 pt-1 border-t border-[#F1F5F9]">
          <p className="text-[10px] text-[#94A3B8]">
            Estos tres niveles se convierten en instrucciones claras para la IA. No reemplazan objetivos ni límites — los complementan.
          </p>
          {LEVEL_FIELDS.map((field) => (
            <LevelSelector
              key={field.key}
              field={field}
              value={form[field.key]}
              onChange={(n) => setForm((f) => ({ ...f, [field.key]: n }))}
              labels={(profile?.level_labels || {})[field.key.replace('_level', '')]}
            />
          ))}
        </div>
        <div>
          <label className="text-xs font-medium text-[#64748B] mb-1 block">Rasgos de personalidad ({form.personality_traits.length}/20)</label>
          <div className="flex gap-1 flex-wrap mb-2">{form.personality_traits.map((t, i) => (
            <span key={i} className="text-[10px] bg-[#F1F5F9] border border-[#E2E8F0] rounded-full px-2 py-0.5 flex items-center gap-1">
              {t} <button onClick={() => setForm(f => ({ ...f, personality_traits: f.personality_traits.filter((_, j) => j !== i) }))}><X className="w-2.5 h-2.5" /></button>
            </span>
          ))}</div>
          <div className="flex gap-1"><Input value={newTrait} onChange={e => setNewTrait(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTrait()} placeholder="Agregar rasgo..." className="text-xs flex-1" maxLength={80} />
            <Button size="sm" onClick={addTrait} className="text-xs bg-[#0F172A]">+</Button></div>
        </div>
        <div><label className="text-xs font-medium text-[#64748B] mb-1 block">System prompt personalizado (opcional)</label>
          <textarea value={form.system_prompt_template} onChange={e => setForm(f => ({ ...f, system_prompt_template: e.target.value }))} rows={5} maxLength={12000}
            className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-2 font-mono" placeholder="Instrucciones adicionales... usa {{current_date}}, {{tenant_name}}, etc." data-testid="system-prompt-input" />
          <div className="flex gap-1 mt-1 flex-wrap">{VAR_HINTS.map(v => (<span key={v} className="text-[10px] bg-[#F8FAFC] border border-[#E2E8F0] rounded px-1 py-0.5 font-mono cursor-pointer hover:bg-[#F1F5F9]" onClick={() => setForm(f => ({ ...f, system_prompt_template: (f.system_prompt_template || '') + `{{${v}}}` }))}>{`{{${v}}}`}</span>))}</div>
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="bg-[#0F172A] text-white text-xs" data-testid="save-identity-btn"><Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Guardando...' : 'Guardar Identidad'}</Button>
    </div>
  );
}

function ObjectivesTab({ profile, setProfile }) {
  const [objectives, setObjectives] = useState(profile?.objectives || []);
  const [newObj, setNewObj] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/tenant/ai-training/profile', { objectives });
      setProfile(prev => ({ ...prev, ...data })); toast.success('Objetivos guardados');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="objectives-tab">
      <SectionHelp module="objectives" title="los objetivos"
        description="Cada objetivo es una instrucción directa que la IA sigue en TODAS sus conversaciones, sin importar el canal. Ejemplos: 'Siempre pregunta el nombre del cliente antes de continuar', 'Prioriza agendar una cita sobre solo dar información', 'Menciona la promoción del mes si el cliente pregunta por precios'. Entre más claro y específico, mejor los sigue."
        whatItDoesNot="No reemplaza tu conocimiento de negocio (eso va en Biblioteca) ni controla el tono de la personalidad (eso va en Identidad)." />
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-semibold text-[#0F172A]">Objetivos del asistente ({objectives.length}/20)</h3>
        {objectives.map((o, i) => (
          <div key={i} className="flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2">
            <span className="text-xs text-[#0F172A] flex-1">{o}</span>
            <button onClick={() => setObjectives(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <div className="flex gap-1"><Input value={newObj} onChange={e => setNewObj(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && newObj.trim()) { setObjectives(p => [...p, newObj.trim()]); setNewObj(''); }}} placeholder="Nuevo objetivo..." className="text-xs flex-1" maxLength={300} />
          <Button size="sm" onClick={() => { if (newObj.trim()) { setObjectives(p => [...p, newObj.trim()]); setNewObj(''); }}} className="text-xs bg-[#0F172A]">+</Button></div>
      </div>
      <Button onClick={save} disabled={saving} className="bg-[#0F172A] text-white text-xs" data-testid="save-objectives-btn"><Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Guardando...' : 'Guardar Objetivos'}</Button>
    </div>
  );
}

function GuardrailsTab({ profile, setProfile }) {
  const [guardrails, setGuardrails] = useState(profile?.guardrails || []);
  const [newG, setNewG] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/tenant/ai-training/profile', { guardrails });
      setProfile(prev => ({ ...prev, ...data })); toast.success('Límites guardados');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="guardrails-tab">
      <SectionHelp module="guardrails" title="las reglas y límites"
        description="Son cosas que la IA NUNCA debe hacer, sin importar qué tan convincente sea el cliente. Ejemplos: 'Nunca confirmes una cita sin verificarla en el calendario', 'Nunca des el número personal de un empleado', 'Nunca ofrezcas descuentos no autorizados'. Se aplican como un límite duro por encima de todo lo demás — incluso si el cliente insiste."
        whatItDoesNot="No hace que la IA sea más estricta o menos amigable en general — solo bloquea las acciones específicas que agregues aquí." />
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-semibold text-[#0F172A]">Reglas y límites ({guardrails.length}/30)</h3>
        {guardrails.map((g, i) => (
          <div key={i} className="flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2">
            <Shield className="w-3 h-3 text-red-400 flex-shrink-0" />
            <span className="text-xs text-[#0F172A] flex-1">{g}</span>
            <button onClick={() => setGuardrails(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <div className="flex gap-1"><Input value={newG} onChange={e => setNewG(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && newG.trim()) { setGuardrails(p => [...p, newG.trim()]); setNewG(''); }}} placeholder="Nueva regla..." className="text-xs flex-1" maxLength={300} />
          <Button size="sm" onClick={() => { if (newG.trim()) { setGuardrails(p => [...p, newG.trim()]); setNewG(''); }}} className="text-xs bg-[#0F172A]">+</Button></div>
      </div>
      <Button onClick={save} disabled={saving} className="bg-[#0F172A] text-white text-xs" data-testid="save-guardrails-btn"><Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Guardando...' : 'Guardar Límites'}</Button>
    </div>
  );
}

function ChannelsTab({ profile }) {
  const [rules, setRules] = useState(profile?.channel_rules || []);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    prompt_addon: '', max_output_tokens: 500, temperature: 0.7,
    intent_detection_enabled: false,
  });

  const save = async (channel) => {
    try {
      const rule = rules.find(r => r.channel === channel);
      const config = {
        ...(rule?.config || {}),
        intent_detection_enabled: !!form.intent_detection_enabled,
      };
      const { data } = await api.put(`/tenant/ai-training/channel-rules/${channel}`, {
        ...form,
        config,
      });
      setRules(prev => { const idx = prev.findIndex(r => r.channel === channel); return idx >= 0 ? prev.map((r, i) => i === idx ? data : r) : [...prev, data]; });
      setEditing(null); toast.success(`Canal ${friendlyChannelName(channel)} guardado`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="channels-tab">
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-semibold text-[#0F172A]">Reglas por Canal</h3>
        <p className="text-xs text-[#64748B]">
          Cada canal (WhatsApp, llamadas, email...) tiene su propia naturaleza — una llamada necesita más margen para responder que un mensaje corto. Lo que configures aquí ahora sí se aplica en cada respuesta real, no solo en el simulador.
        </p>
        {CHANNELS.map(ch => {
          const rule = rules.find(r => r.channel === ch);
          const isEditing = editing === ch;
          return (
            <div key={ch} className="border border-[#E2E8F0] rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><MessageCircle className="w-3.5 h-3.5 text-[#94A3B8]" /><span className="text-xs font-medium text-[#0F172A]">{friendlyChannelName(ch)}</span>
                  {rule && <span className="text-[10px] bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded">configurado</span>}
                </div>
                <Button size="sm" variant="outline" onClick={() => {
                  setEditing(isEditing ? null : ch);
                  if (!isEditing && rule) setForm({
                    prompt_addon: rule.prompt_addon || '',
                    max_output_tokens: rule.max_output_tokens || CHANNEL_DEFAULT_MAX_TOKENS[ch] || 500,
                    temperature: rule.temperature ?? CHANNEL_DEFAULT_TEMPERATURE[ch] ?? 0.7,
                    intent_detection_enabled: !!(rule.config?.intent_detection_enabled),
                  });
                  else setForm({
                    prompt_addon: '',
                    max_output_tokens: CHANNEL_DEFAULT_MAX_TOKENS[ch] || 500,
                    temperature: CHANNEL_DEFAULT_TEMPERATURE[ch] ?? 0.7,
                    intent_detection_enabled: false,
                  });
                }} className="text-[10px]">
                  {isEditing ? 'Cerrar' : 'Editar'}
                </Button>
              </div>
              {isEditing && (
                <div className="mt-3 space-y-2">
                  <textarea value={form.prompt_addon} onChange={e => setForm(f => ({ ...f, prompt_addon: e.target.value }))} rows={3} maxLength={4000} placeholder="Instrucciones adicionales para este canal..." className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-2 font-mono" />
                  <div className="flex gap-4">
                    <div>
                      <label className="text-[10px] text-[#94A3B8]">Tope máximo de respuesta (tokens)</label>
                      <Input type="number" value={form.max_output_tokens} onChange={e => setForm(f => ({ ...f, max_output_tokens: parseInt(e.target.value) || CHANNEL_DEFAULT_MAX_TOKENS[ch] || 500 }))} className="text-xs w-24" min={50} max={4000} />
                      <p className="text-[10px] text-[#CBD5E1] mt-0.5 max-w-[180px]">No es el largo objetivo, es el límite de emergencia para que nunca se corte a la mitad. {ch === 'voice' && 'En llamadas no suele hacer falta tocarlo.'}</p>
                    </div>
                    <div><label className="text-[10px] text-[#94A3B8]">Temperatura</label><Input type="number" value={form.temperature} onChange={e => setForm(f => ({ ...f, temperature: parseFloat(e.target.value) || 0.7 }))} className="text-xs w-20" min={0} max={1.5} step={0.1} /></div>
                  </div>
                  {(ch === 'whatsapp' || ch === 'voice') && (
                    <div className="flex items-center justify-between gap-3 pt-1 border-t border-[#F1F5F9]">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-[#0F172A]">Detectar intención en mensajes</span>
                        <HelpTooltip text="La IA clasifica cada mensaje entrante (alta intención, objeción de precio, objeción de tiempo o neutro) y dispara automatizaciones si las configuras. Apagado por defecto — es una llamada extra de IA por mensaje." />
                      </div>
                      <Switch
                        checked={!!form.intent_detection_enabled}
                        onCheckedChange={(v) => setForm(f => ({ ...f, intent_detection_enabled: v }))}
                        data-testid={`intent-detection-toggle-${ch}`}
                      />
                    </div>
                  )}
                  <Button size="sm" onClick={() => save(ch)} className="text-xs bg-[#0F172A]"><Save className="w-3 h-3 mr-1" />Guardar</Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SimulatorTab({ profile, setProfile }) {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [channel, setChannel] = useState('');
  const [simMode, setSimMode] = useState('text');
  const [result, setResult] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState(null);
  const [workflowBusy, setWorkflowBusy] = useState(false);
  const [showAb, setShowAb] = useState(false);
  const [abResult, setAbResult] = useState(null);
  const [abBusy, setAbBusy] = useState(false);

  const simulate = async () => {
    if (!message.trim()) return;
    setSimulating(true);
    try {
      const endpoint = simMode === 'voice'
        ? '/tenant/ai-training/simulate-voice'
        : simMode === 'tools'
          ? '/tenant/ai-training/simulate-tools'
          : '/tenant/ai-training/simulate';
      const { data } = await api.post(endpoint, { message, channel: channel || (simMode === 'voice' ? 'voice' : undefined) });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setResult({ ok: false, error: formatApiError(e.response?.data?.detail) }); }
    finally { setSimulating(false); }
  };

  const submitReview = async () => {
    setWorkflowBusy(true);
    try {
      const { data } = await api.post('/tenant/ai-training/submit-review');
      setProfile((prev) => ({ ...prev, ...data }));
      toast.success('Perfil enviado a revisión');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setWorkflowBusy(false); }
  };

  const publish = async () => {
    setWorkflowBusy(true);
    try {
      await api.post('/tenant/ai-training/publish');
      const { data } = await api.get('/tenant/ai-training/profile');
      setProfile(data);
      toast.success('Perfil publicado');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setWorkflowBusy(false); }
  };

  const preview = async () => {
    try {
      const { data } = await api.post('/tenant/ai-training/preview', { channel: channel || undefined });
      setPreviewPrompt(data.system_prompt);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const compareAb = async () => {
    if (!message.trim()) return;
    setAbBusy(true);
    setAbResult(null);
    try {
      const { data } = await api.post('/tenant/ai-training/compare-ab', {
        message,
        channel: channel || 'whatsapp',
        compare_type: 'training_profile',
        variant_a: 'draft',
        variant_b: (profile?.current_version || 0) > 0 ? 'published' : 'draft',
      });
      setAbResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAbBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="simulator-tab">
      <div className="flex flex-wrap gap-2 items-center">
        <Button size="sm" variant="outline" onClick={preview} className="text-xs gap-1" data-testid="preview-prompt-btn"><Eye className="w-3 h-3" />Ver prompt</Button>
        {profile?.status !== 'pending_review' && (
          <Button size="sm" variant="outline" onClick={submitReview} disabled={workflowBusy || !profile?.is_persisted} className="text-xs gap-1" data-testid="submit-review-btn">
            <Clock className="w-3 h-3" />Enviar a revisión
          </Button>
        )}
        {profile?.status === 'pending_review' && canPublishProfile(user) && (
          <Button size="sm" variant="outline" onClick={publish} disabled={workflowBusy} className="text-xs gap-1" data-testid="publish-btn">
            <Upload className="w-3 h-3" />Aprobar y publicar
          </Button>
        )}
        {profile?.status === 'pending_review' && !canPublishProfile(user) && (
          <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            En revisión — solo el dueño o administrador puede publicar
          </span>
        )}
      </div>

      {previewPrompt && (
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
          <div className="flex justify-between mb-2"><span className="text-xs font-semibold text-[#64748B]">System Prompt</span><button onClick={() => setPreviewPrompt(null)}><X className="w-3.5 h-3.5 text-[#94A3B8]" /></button></div>
          <pre className="text-xs text-[#0F172A] whitespace-pre-wrap font-mono" data-testid="preview-prompt-text">{previewPrompt}</pre>
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-[#0F172A]">Simulador</h3>
          <HelpTooltip text="Prueba el mismo mensaje en tres modos: texto (chat), voz (un turno de llamada sin marcar) y herramientas (muestra qué haría la IA sin ejecutar acciones reales)." />
        </div>
        <div className="flex gap-1 flex-wrap">
          {[
            { k: 'text', l: 'Texto' },
            { k: 'voice', l: 'Voz' },
            { k: 'tools', l: 'Herramientas' },
          ].map((m) => (
            <button
              key={m.k}
              type="button"
              onClick={() => { setSimMode(m.k); setResult(null); }}
              className={`text-[10px] px-2.5 py-1 rounded-full border ${
                simMode === m.k ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-[#64748B] border-[#E2E8F0]'
              }`}
              data-testid={`sim-mode-${m.k}`}
            >
              {m.l}
            </button>
          ))}
        </div>
        {simMode === 'tools' && (
          <p className="text-[10px] text-[#94A3B8]">
            Usa las herramientas activadas en Config IA → Canales. Si ninguna está encendida, te pedirá activarlas primero.
          </p>
        )}
        {simMode === 'voice' && (
          <p className="text-[10px] text-[#94A3B8]">
            Simula un turno de llamada con el mismo prompt de voz de producción. No se realiza ninguna llamada real.
          </p>
        )}
        <div className="flex gap-2">
          <select value={channel} onChange={e => setChannel(e.target.value)} className="text-xs border border-[#E2E8F0] rounded-lg px-2 py-1.5" disabled={simMode === 'voice'}>
            <option value="">{simMode === 'voice' ? 'Canal voz (fijo)' : 'Canal general'}</option>
            {CHANNELS.map(ch => <option key={ch} value={ch}>{friendlyChannelName(ch)}</option>)}
          </select>
        </div>
        <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} maxLength={4000} placeholder="Escribe un mensaje para probar el asistente..." className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid="simulate-message-input" />
        <div className="flex gap-2">
          <Button onClick={simulate} disabled={simulating || !message.trim()} className="bg-[#F72585] text-white text-xs flex-1" data-testid="simulate-btn">
            <Play className="w-3.5 h-3.5 mr-1" />{simulating ? 'Simulando...' : `Simular ${simMode === 'voice' ? 'voz' : simMode === 'tools' ? 'herramientas' : 'texto'}`}
          </Button>
          {simMode === 'text' && (
            <Button variant="outline" size="sm" onClick={() => setShowAb(v => !v)} className="text-xs" data-testid="training-ab-toggle">
              A/B
            </Button>
          )}
        </div>
        {showAb && simMode === 'text' && (
          <div className="border border-[#E2E8F0] rounded-lg p-3 bg-[#F8FAFC] space-y-2" data-testid="training-ab-panel">
            <div className="flex items-center gap-1">
              <p className="text-[10px] text-[#64748B]">Compara borrador vs versión publicada del perfil con el mismo mensaje.</p>
              <HelpTooltip text="Útil antes de publicar: ves lado a lado cómo cambió la respuesta. No guarda nada en producción ni envía mensajes reales." />
            </div>
            <Button size="sm" variant="outline" onClick={compareAb} disabled={abBusy || !message.trim()} data-testid="training-ab-run">
              {abBusy ? 'Comparando...' : 'Comparar A/B'}
            </Button>
            {abResult?.variant_a && abResult?.variant_b && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[abResult.variant_a, abResult.variant_b].map((v) => (
                  <div key={v.side} className="bg-white border border-[#E2E8F0] rounded-lg p-3" data-testid={`training-ab-${v.side}`}>
                    <p className="text-[10px] font-semibold text-[#64748B] mb-1">{v.label}</p>
                    <p className="text-xs text-[#0F172A] whitespace-pre-wrap">{v.text || v.error || '—'}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {result && (
          <div className={`p-4 rounded-lg ${result.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'} border`} data-testid="simulate-result">
            {result.ok ? (
              <>
                <p className="text-sm text-[#0F172A] whitespace-pre-wrap">{result.text}</p>
                {result.tool_execution && (
                  <div className="mt-2 text-[10px] text-blue-700 bg-blue-50 border border-blue-100 rounded p-2" data-testid="tool-simulation-result">
                    <p className="font-medium">Herramienta simulada: {result.tool_execution.name}</p>
                    {result.tool_execution.result?.message && <p className="mt-1">{result.tool_execution.result.message}</p>}
                  </div>
                )}
                <div className="flex gap-3 mt-2 text-[10px] text-[#94A3B8]">
                  <span>{friendlyAiProviderName(result.provider_key)}</span><span>{(result.input_tokens || 0) + (result.output_tokens || 0)} tokens</span><span>{result.latency_ms}ms</span>
                  {result.simulation_mode && <span>modo {result.simulation_mode}</span>}
                  {result.billing_recorded === false && <span className="text-amber-500">Consumo no registrado</span>}
                </div>
              </>
            ) : (
              <p className="text-xs text-red-600">{result.error}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function VersionsTab() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/tenant/ai-training/versions').then(r => setVersions(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const restore = async (id, num) => {
    try {
      await api.post(`/tenant/ai-training/versions/${id}/restore-draft`);
      toast.success(`Versión ${num} restaurada como borrador`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="versions-tab">
      <h3 className="text-sm font-semibold text-[#0F172A]">Historial de Versiones</h3>
      {/* Sprint 53 (13/N) — antes se veía "roto" para cualquier tenant que
          nunca completó el flujo real de publicación (Enviar a revisión →
          Publicar, esto último solo Owner/Admin) — nunca explicaba por qué
          la lista está vacía. */}
      {versions.length === 0 ? (
        <p className="text-xs text-[#94A3B8] text-center py-6 max-w-md mx-auto leading-relaxed">
          Aún no hay versiones publicadas. Una versión se crea cuando envías tu configuración a revisión (pestaña "Perfil") y luego el dueño o un administrador la publica — antes de eso, tus cambios quedan como borrador y no aparecen aquí.
        </p>
      ) : (
        <div className="space-y-2">{versions.map(v => (
          <div key={v.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-[#0F172A]">v{v.version_number}</span>
              <span className="text-[10px] text-[#94A3B8] ml-2">{v.published_at ? new Date(v.published_at).toLocaleString('es-MX') : ''}</span>
            </div>
            <Button size="sm" variant="outline" onClick={() => restore(v.id, v.version_number)} className="text-[10px] gap-1" data-testid={`restore-version-${v.version_number}`}>
              <RotateCcw className="w-3 h-3" /> Restaurar
            </Button>
          </div>
        ))}</div>
      )}
    </div>
  );
}
